/**
 * Chat Notes Hook
 *
 * Allows users to save chat conversations as notes for later reference.
 * Notes are stored locally and can be exported or shared.
 */
const CHAT_NOTES_STORAGE_KEY = 'royaldiamonds-chat-notes';
const MAX_NOTES = 50;
/**
 * Generate a unique ID
 */
function generateId() {
    return `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
/**
 * Load all saved notes from localStorage
 */
export function loadChatNotes() {
    try {
        const stored = localStorage.getItem(CHAT_NOTES_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    }
    catch (error) {
        console.error('Failed to load chat notes:', error);
        return [];
    }
}
/**
 * Save notes to localStorage
 */
function saveChatNotes(notes) {
    try {
        localStorage.setItem(CHAT_NOTES_STORAGE_KEY, JSON.stringify(notes));
    }
    catch (error) {
        console.error('Failed to save chat notes:', error);
    }
}
/**
 * Create a new note from a conversation
 */
export function createChatNote(title, conversation, options) {
    const note = {
        id: generateId(),
        title,
        summary: options?.summary || generateSummary(conversation),
        fullConversation: conversation,
        tags: options?.tags || extractTags(conversation),
        createdAt: new Date().toISOString(),
        pageContext: options?.pageContext,
    };
    const notes = loadChatNotes();
    // Add new note at the beginning
    notes.unshift(note);
    // Keep only the most recent notes
    if (notes.length > MAX_NOTES) {
        notes.splice(MAX_NOTES);
    }
    saveChatNotes(notes);
    return note;
}
/**
 * Delete a note by ID
 */
export function deleteChatNote(noteId) {
    const notes = loadChatNotes();
    const index = notes.findIndex(n => n.id === noteId);
    if (index === -1)
        return false;
    notes.splice(index, 1);
    saveChatNotes(notes);
    return true;
}
/**
 * Generate a summary from conversation
 */
function generateSummary(conversation) {
    // Take the first user question as a summary base
    const firstQuestion = conversation.find(m => m.role === 'user');
    if (!firstQuestion)
        return 'Chat conversation';
    // Truncate to reasonable length
    const summary = firstQuestion.content.substring(0, 150);
    return summary.length < firstQuestion.content.length ? `${summary}...` : summary;
}
/**
 * Extract relevant tags from conversation content
 */
function extractTags(conversation) {
    const tags = new Set();
    const content = conversation.map(m => m.content.toLowerCase()).join(' ');
    // Diamond-related tags
    const diamondTerms = [
        'diamond', 'carat', 'clarity', 'color', 'cut',
        'round', 'princess', 'oval', 'emerald', 'cushion', 'marquise', 'pear',
        'gia', 'certification', 'certificate',
        'vvs', 'vs', 'si', 'fl', 'if',
        '4cs', 'four cs',
    ];
    // Topic tags
    const topicTerms = {
        'price': 'pricing',
        'cost': 'pricing',
        'budget': 'pricing',
        'appraisal': 'appraisal',
        'care': 'jewelry care',
        'clean': 'jewelry care',
        'maintenance': 'jewelry care',
        'engagement': 'engagement rings',
        'wedding': 'wedding rings',
        'ring': 'rings',
        'necklace': 'necklaces',
        'bracelet': 'bracelets',
        'earring': 'earrings',
        'setting': 'settings',
        'mount': 'settings',
    };
    // Check for diamond terms
    diamondTerms.forEach(term => {
        if (content.includes(term)) {
            tags.add(term === '4cs' || term === 'four cs' ? '4Cs' : term);
        }
    });
    // Check for topic terms
    Object.entries(topicTerms).forEach(([term, tag]) => {
        if (content.includes(term)) {
            tags.add(tag);
        }
    });
    // Return up to 5 tags
    return Array.from(tags).slice(0, 5);
}
/**
 * Export a note as text
 */
export function exportNoteAsText(note) {
    const lines = [
        `=== ${note.title} ===`,
        `Date: ${new Date(note.createdAt).toLocaleDateString()}`,
        note.pageContext ? `Page: ${note.pageContext}` : '',
        note.tags.length > 0 ? `Tags: ${note.tags.join(', ')}` : '',
        '',
        '--- Summary ---',
        note.summary,
        '',
        '--- Full Conversation ---',
        '',
    ];
    note.fullConversation.forEach(msg => {
        const speaker = msg.role === 'user' ? 'You' : 'Royal Diamonds AI';
        lines.push(`${speaker}:`);
        lines.push(msg.content);
        lines.push('');
    });
    lines.push('---');
    lines.push('Saved from Royal Diamonds Chat Assistant');
    return lines.filter(Boolean).join('\n');
}
/**
 * Search notes by query
 */
export function searchChatNotes(query) {
    const notes = loadChatNotes();
    const lowerQuery = query.toLowerCase();
    return notes.filter(note => note.title.toLowerCase().includes(lowerQuery) ||
        note.summary.toLowerCase().includes(lowerQuery) ||
        note.tags.some(tag => tag.toLowerCase().includes(lowerQuery)) ||
        note.fullConversation.some(msg => msg.content.toLowerCase().includes(lowerQuery)));
}
export default {
    loadChatNotes,
    createChatNote,
    deleteChatNote,
    exportNoteAsText,
    searchChatNotes,
};
